import React, { useMemo, useState } from "react";

import jsonExport from "jsonexport/dist";
import classnames from "classnames";

import { Grid, Typography, useMediaQuery } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";

import {
  Button,
  Confirm,
  Datagrid,
  downloadCSV,
  FunctionField,
  TextField,
  useListContext,
  useNotify,
} from "react-admin";

import { currencyFormatter, taxRateRenderer } from "../../../utils/utils";
import { getUnitName } from "../../../utils/loadTypeUtils";
import {
  ResourceType,
  ServiceHeaderMap,
  UnitOptions,
} from "../../../utils/constants";
import {
  isSubscriptionDisabled,
  logError,
  logHistory,
} from "../../../utils/appUtils";

import { DeleteIcon, EditIcon } from "../../../components/common/iconography";
import SimpleList from "../../../components/common/SimpleList";
import { Pagination } from "../../../components/Pagination";
import { SearchField } from "../../../components/common/SearchField";
import { useDeleteButtonStyles } from "../../../components/common/useDeleteButtonStyles";
import { ServiceStatusIcon } from "../services/ServiceStatusIcon";
import { bulkDeleteServices } from "./ServiceUtils";

const useStyles = makeStyles((theme) => ({
  searchField: {
    minWidth: "16rem",
    marginTop: theme.spacing(3),
  },
}));

const ServiceSearchField = ({ allServices, setServices, label, alwaysOn }) => {
  const classes = useStyles();

  const handleSearch = (value) => {
    const searchTerm = value?.toLowerCase() || "";
    const newServices = allServices.filter(
      (itm) =>
        itm.name?.toLowerCase().includes(searchTerm) ||
        itm.description?.toLowerCase().includes(searchTerm),
    );
    setServices(newServices);
  };

  return (
    <SearchField
      id="searchTerm"
      label={label}
      margin="dense"
      variant="outlined"
      alwaysOn={alwaysOn}
      className={classes.searchField}
      handleSearch={handleSearch}
      searchOnChange={true}
    />
  );
};

export const getListFilters = (allServices, setServices) => [
  <ServiceSearchField
    label="Service Search"
    allServices={allServices}
    setServices={setServices}
    alwaysOn
  />,
];

export const exportServicesToCsv = (services, fileNamePrefix) => {
  const servicesForExport = services.map((service) => {
    const serviceExport = {
      name: service.name,
      description: service.description,
      status: service.status,
      // Add tax rate as separate columns
      taxCode: service.taxRate?.taxCode || "",
      taxRate: service.taxRate?.taxRate || "0",
    };

    // Dynamically add unit rate fields
    UnitOptions.forEach((unit) => {
      serviceExport[unit.column] = service[unit.column] || 0;
    });

    return serviceExport;
  });

  jsonExport(
    servicesForExport,
    {
      rename: Object.keys(ServiceHeaderMap),
    },
    (err, csv) => {
      downloadCSV(csv, `${fileNamePrefix}_services`);
    },
  );
};

export const PaginationToolbar = (props) => {
  const listContext = useListContext(props);

  return (
    <Grid container direction="row" spacing={1} alignItems="center">
      <Grid item xs={4}>
        <Typography>
          {listContext?.selectedIds?.length ? (
            `${listContext.selectedIds.length} selected`
          ) : (
            <>&nbsp;</>
          )}
        </Typography>
      </Grid>
      <Grid item xs={8}>
        <Pagination {...props} {...listContext} />
      </Grid>
    </Grid>
  );
};

const buildDeleteSummary = ({ hardDeleted, softDeleted, blocked, failed }) => {
  const parts = [];
  if (hardDeleted.length) parts.push(`${hardDeleted.length} deleted`);
  if (softDeleted.length)
    parts.push(`${softDeleted.length} deactivated (already billed)`);
  if (blocked.length) {
    const reasons = [...new Set(blocked.map(({ reason }) => reason))];
    parts.push(`${blocked.length} skipped (${reasons.join(", ")})`);
  }
  if (failed.length) parts.push(`${failed.length} failed`);
  return parts.length ? parts.join(", ") : "No services deleted";
};

export const BulkDeleteServicesButton = ({
  allServices = [],
  isYardTemplate = false,
  handleRefresh,
}) => {
  const classes = useDeleteButtonStyles();
  const notify = useNotify();
  const { selectedIds, onUnselectItems } = useListContext();
  const [open, setOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // Resolve against the full service list, not useList's paginated `data`
  // slice: selection survives page and filter changes, so off-page ids must
  // still delete.
  const servicesById = useMemo(
    () => new Map(allServices.map((service) => [service.id, service])),
    [allServices],
  );

  const handleDelete = async () => {
    setIsDeleting(true);
    try {
      const ids = selectedIds || [];
      const services = ids.map((id) => servicesById.get(id)).filter(Boolean);
      const outcome = await bulkDeleteServices(services);
      const unresolved = ids
        .filter((id) => !servicesById.has(id))
        .map((id) => ({
          service: { id },
          reason: new Error("Service not found"),
        }));
      outcome.failed = [...outcome.failed, ...unresolved];

      // Hard deletes are logged on the account, matching the single-service
      // delete button. Yard template services have no account to log against.
      if (!isYardTemplate && outcome.hardDeleted.length) {
        const namesByAccount = new Map();
        outcome.hardDeleted.forEach((service) => {
          const names = namesByAccount.get(service.accountId) || [];
          names.push(service.name);
          namesByAccount.set(service.accountId, names);
        });
        namesByAccount.forEach((names, accountId) => {
          if (!accountId) return;
          logHistory({
            parentId: accountId,
            parentType: ResourceType.ACCOUNT,
            text:
              names.length === 1
                ? `Service ${names[0]} deleted.`
                : `${names.length} Services deleted: ${names.join("; ")}.`,
          });
        });
      }

      // Soft-deleted services keep their own record, so they log against
      // themselves - again matching the single-service delete button.
      outcome.softDeleted.forEach((service) =>
        logHistory({
          parentId: service.id,
          parentType: ResourceType.SERVICE,
          text: "Service deleted.",
        }),
      );

      const summary = buildDeleteSummary(outcome);
      if (outcome.failed.length) {
        notify(summary, "warning");
        logError(
          "ServicesTabShared:BulkDeleteServicesButton",
          new Error(
            `Failed to delete ${outcome.failed.length} service(s): ` +
              outcome.failed
                .map(
                  ({ service, reason }) =>
                    `${service?.id || "(no id)"}: ${reason?.message || reason}`,
                )
                .join("; "),
          ),
          false,
        );
      } else {
        notify(summary, outcome.blocked.length ? "warning" : "success");
      }

      onUnselectItems();
      handleRefresh && handleRefresh();
    } catch (error) {
      logError("ServicesTabShared:BulkDeleteServicesButton", error, notify);
    } finally {
      setIsDeleting(false);
      setOpen(false);
    }
  };

  return (
    <>
      <Button
        label="Delete"
        size="small"
        disabled={isDeleting || isSubscriptionDisabled()}
        onClick={() => setOpen(true)}
        className={classnames("ra-delete-button", classes.deleteButton)}
      >
        <DeleteIcon />
      </Button>
      <Confirm
        isOpen={open}
        loading={isDeleting}
        title="Delete Confirmation"
        content={`Delete ${selectedIds.length} Service${
          selectedIds.length === 1 ? "" : "s"
        }? You will not lose any data. Services already used on invoices or in past billing rules are deactivated instead, and services used in active Billing Rules are skipped.`}
        onConfirm={handleDelete}
        onClose={() => setOpen(false)}
      />
    </>
  );
};

const renderTaxRate = (record) => {
  if (record?.taxRate) return taxRateRenderer(record.taxRate);
  return "";
};

const EditButton = () => {
  return (
    <Button label="Edit" size="small" color="primary">
      <EditIcon />
    </Button>
  );
};

export const ServicesRatesDatagrid = ({
  allServices,
  onOpenService,
  onEdit,
  xsDown,
  enableSelection = false,
}) => {
  const lgDown = useMediaQuery((theme) => theme.breakpoints.down("lg"));

  // Analyze the services to determine which units have rates set
  const unitsWithRates = useMemo(() => {
    // Initialize an object to keep track of units with rates
    const units = {};

    // Iterate over each service
    allServices?.forEach((service) => {
      UnitOptions.forEach((unit) => {
        const rate = service[unit.column];
        if (rate != null && rate !== 0) {
          units[unit.id] = unit;
        }
      });
    });

    // Return an array of units that have rates set
    return Object.values(units);
  }, [allServices]);

  return xsDown ? (
    <SimpleList
      primaryText={(record) => record.name}
      secondaryText={(record) => record.description}
      tertiaryText={(record) => renderTaxRate(record)}
      leftIcon={(record) => <ServiceStatusIcon record={record} />}
      rightIcon={() => (
        <EditIcon
          fontSize="large"
          color="secondary"
          style={{ marginTop: 10 }}
        />
      )}
      linkType={false}
      rowClick={onEdit}
    />
  ) : lgDown ? (
    <Datagrid rowClick={onOpenService} hasBulkActions={enableSelection}>
      <ServiceStatusIcon />
      <TextField source="name" label="Service Name" />
      <FunctionField
        source="taxRate.taxCode"
        label="Tax Rate"
        render={renderTaxRate}
      />
      {/* Dynamically render unit columns */}
      {unitsWithRates.map((unit) => (
        <FunctionField
          key={unit.id}
          source={unit.column}
          label={getUnitName(unit.id)}
          render={(record) => {
            const value = record?.[unit.column];
            return value == null || value === 0
              ? ""
              : currencyFormatter.format(value);
          }}
          sortable={false}
        />
      ))}
    </Datagrid>
  ) : (
    <Datagrid rowClick={onOpenService} hasBulkActions={enableSelection}>
      <ServiceStatusIcon />
      <TextField source="name" label="Service Name" />
      <TextField source="description" label="Description" />
      <FunctionField
        source="taxRate.taxCode"
        label="Tax Rate"
        render={renderTaxRate}
      />
      {/* Dynamically render unit columns */}
      {unitsWithRates.map((unit) => (
        <FunctionField
          key={unit.id}
          source={unit.column}
          label={getUnitName(unit.id)}
          render={(record) => {
            const value = record?.[unit.column];
            return value == null || value === 0
              ? ""
              : currencyFormatter.format(value);
          }}
          sortable={false}
        />
      ))}
      <EditButton />
    </Datagrid>
  );
};
